import React, { ReactElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { Box, HBox, VBox } from "../box/Box";
import Spacer from "../spacer/Spacer";
import { Typo } from "../text/Typo";
import { ITheme } from "../theme/GlobalTheme";
import { useWindowSize } from "../utils/useResize";

// STYLES
export const getDefaultTabsLightTheme = {
	backgroundHover: "rgb(206, 215, 235)",
};

export const getDefaultTabsDarkTheme: typeof getDefaultTabsLightTheme = {
	backgroundHover: "rgb(206, 215, 235)",
};

export interface ITabsProps {
	children?: (ReactElement<ITabItemProps> | null | false)[] | ReactElement<ITabItemProps>;
	selected?: number;
	sx?: React.CSSProperties;
	scrollable?: boolean;
	background?: string;
	spaceLeft?: number;
	spaceBottom?: number;
	rounds?: boolean;
	decoration?: React.ReactElement;
	title?: string;
	onSectionSelect?: (section: number) => void;
	checkFeature?: (feature: string) => boolean;
	variant?: "modern" | "round" | "strong";
}

export interface ITabItemProps {
	title: string;
	children?: any;
	section?: number;
	type?: "default" | "spacer" | "button";
	onClick?: () => void;
	sx?: React.CSSProperties;
}

export const Tabs = ({
	children,
	scrollable,
	background,
	selected,
	sx,
	spaceLeft,
	spaceBottom,
	rounds,
	decoration,
	title,
	checkFeature,
	onSectionSelect,
	variant,
}: ITabsProps) => {
	if (selected === undefined || isNaN(selected)) selected = 0;

	if (children) {
		if (!Array.isArray(children) && React.isValidElement(children)) children = [children];
	}

	const hiddenChilds: any[] = [];

	if (children && checkFeature) {
		children.map(val => {
			if (!val) return null;
			React.Children.toArray(val.props.children).forEach(child => {
				if (!React.isValidElement(child)) return true;
				const component = child.type;
				const feature = (component as any).feature;
				if (feature) {
					// check feature
					if (checkFeature && !checkFeature(feature)) {
						hiddenChilds.push(val);
					}
				}
			});
		});

		//selected = undefined;

		if (hiddenChilds.length > 0 && children.length > 0) {
			for (let index = 0; index < children.length; index++) {
				if (!hiddenChilds.find(fval => fval === children[index])) {
					selected = index;
					break;
				}
			}
		}
	}

	const [currentSelection, setCurrentSelection] = useState(0);

	useEffect(() => {
		if (selected !== undefined) {
			// todo need this line?
			onSelectChange(selected);
			setCurrentSelection(selected);
		}
	}, [selected]);

	const theme = useTheme() as ITheme;
	const style = theme.tabs;
	const { width: viewportWidth } = useWindowSize();
	const isMobile = viewportWidth <= 768;
	const navRef = useRef<HTMLDivElement>(null);
	const [indicator, setIndicator] = useState({ x: 0, y: 0, width: 0, height: 0 });
	useLayoutEffect(() => {
		const nav = navRef.current;
		if (!nav) return;
		const measure = () => {
			const selectedTab = nav.querySelector<HTMLElement>(`[data-tab-index="${currentSelection}"]`);
			if (!selectedTab) return;
			setIndicator({ x: selectedTab.offsetLeft, y: selectedTab.offsetTop, width: selectedTab.offsetWidth, height: selectedTab.offsetHeight });
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(nav);
		return () => observer.disconnect();
	}, [currentSelection, viewportWidth]);
	const slidingIndicator = <Box aria-hidden sx={{
		position: "absolute", pointerEvents: "none", left: 0,
		top: rounds || variant === "round" ? 0 : undefined,
		bottom: rounds || variant === "round" ? undefined : 0,
		width: `${indicator.width}px`, height: rounds || variant === "round" ? `${indicator.height}px` : "3px",
		transform: `translate(${indicator.x}px, ${rounds || variant === "round" ? indicator.y : 0}px)`,
		backgroundColor: "#2B5C8A", borderRadius: rounds || variant === "round" ? "12px" : "99px",
		transition: "transform 320ms var(--v2-ease), width 320ms var(--v2-ease), height 320ms var(--v2-ease)",
	}} />;
	const handleTabKeys = (event: React.KeyboardEvent<HTMLElement>) => {
		if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
		const buttons = Array.from(navRef.current?.querySelectorAll<HTMLElement>("[role='tab']") ?? []);
		const active = buttons.indexOf(event.target as HTMLElement);
		if (active < 0 || !buttons.length) return;
		event.preventDefault();
		const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1
			: (active + (event.key === "ArrowRight" ? 1 : -1) + buttons.length) % buttons.length;
		buttons[next].focus();
		buttons[next].click();
	};

	if (!children) return null;

	const onSelectChange = (index: number) => {
		for (let i = 0; i < children.length; i++) {
			let child = children[i];
			if (child && typeof child === "object" && "props" in child) {
				if (child.props.type === "spacer" || child.props.type === "button") {
					return;
				}
			}
		}

		setCurrentSelection(index);

		if (children.length > index) {
			if (!children[index] || !children[index].props) return;
			if (!React.isValidElement(children[index])) return;
			const { section } = children[index].props;
			onSectionSelect && onSectionSelect(section ?? index);
		}
	};

	let tabs = null;
	if (rounds || variant === "round") {
		tabs = children?.map((val, index) => {
			if (!val) return null;
			if (hiddenChilds && hiddenChilds.find(fval => fval === val)) return null;
			const props = val.props as ITabItemProps;

			if (props.type === "spacer") {
				return <Spacer key={index} grow />;
			}

			return (
				<HBox
					key={index}
					element="button"
					type="button"
					role={props.type === "button" ? "button" : "tab"}
					aria-selected={props.type === "button" ? undefined : currentSelection === index}
					tabIndex={props.type === "button" || currentSelection === index ? 0 : -1}
					data-tab-index={index}
					sx={{
						position: "relative", zIndex: 1, fontFamily: "inherit",
						padding: "6px 16px",
						cursor: "pointer",
						overflow: "hidden",
						backgroundColor: "transparent",
						border: currentSelection === index ? "1px solid rgba(0,0,0,0)" : "1px solid rgba(0,0,0,.1)",
						borderRadius: "12px",
						transition: "background-color .3s, border .3s, color .3s",
						"&:hover": {
							backgroundColor: currentSelection === index ? undefined : "tabsHoverColor",
						},
						...props.sx,
					}}
					onClick={() => {
						if (props.onClick) {
							props.onClick();
							return;
						}

						if (props.type !== "spacer" && props.type !== "button") {
							onSelectChange(index);
							setCurrentSelection(index);
						}
					}}>
					<Typo
						color={currentSelection === index ? "white" : "textPrimary"}
						sx={{
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							fontSize: ".8rem",
							overflow: "hidden",
						}}>
						{val.props && val.props.title}
					</Typo>
				</HBox>
			);
		});
	} else {
		tabs = children?.map((val, index) => {
			if (!val) return null;

			if (hiddenChilds && hiddenChilds.find(fval => fval === val)) return null;

			return (
				<HBox
					key={index}
					element="button"
					type="button"
					role="tab"
					aria-selected={currentSelection === index}
					tabIndex={currentSelection === index ? 0 : -1}
					data-tab-index={index}
					sx={{
						position: "relative", fontFamily: "inherit", border: "none", backgroundColor: "transparent",
						padding: isMobile ? "10px 14px" : variant === "strong" ? "12px 24px" : "15px 20px",
						cursor: "pointer",
						borderBottom: currentSelection === index
							? variant === "strong" ? "3px solid transparent" : "2px solid transparent"
							: variant === "strong" ? "3px solid rgba(0,0,0,.1)" : "2px solid rgba(0,0,0,.1)",
						color: currentSelection === index ? "textPrimary" : "textSecondary",
						transition: "background-color .3s, border .3s, color .3s, border-bottom .3s,color .3s",
						overflow: isMobile ? "visible" : "hidden",
						flexShrink: 0,
						"&:hover": {
							backgroundColor: currentSelection === index ? undefined : style?.backgroundHover,
							color: "textPrimary",
						},
					}}
					onClick={() => {
						onSelectChange(index);
						setCurrentSelection(index);
					}}>
					<Typo
						block
						bold={variant === "strong" ? true : undefined}
						sx={{
							whiteSpace: "nowrap",
							textOverflow: "ellipsis",
							overflow: "hidden",
							fontSize: variant === "modern" ? ".8rem" : variant === "strong" ? ".85rem" : ".9rem",
							...(variant === "strong" ? { textTransform: "uppercase", letterSpacing: "0.03em" } : {}),
						}}>
						{val.props && val.props.title}
					</Typo>
				</HBox>
			);
		});
	}

	if (typeof children[currentSelection] === "boolean") return null;

	let current = currentSelection !== undefined && children[currentSelection]?.props?.children;
	if (!current) return null;

	if (scrollable) {
		current = (
			<Box
				sx={{
					overflow: "auto",
					flexGrow: 1,
					display: "flex",
					flexDirection: "column",
				}}>
				{current}
			</Box>
		);
	} else if (sx) {
		current = <Box>{current}</Box>;
	}

	if (rounds || variant === "round") {
		return (
			<>
				<HBox
					sx={{
						background: background ? background : "transparent",
						marginLeft: spaceLeft ? `${spaceLeft}px` : undefined,
						...sx,
					}}>
					<VBox sx={{ gap: "8px" }}>
						{title && (
							<>
								<Typo variant="small" color="textSecondary" block>
									{title}
								</Typo>
							</>
						)}
						<Box ref={navRef} role="tablist" aria-label={title || "Sections"} onKeyDown={handleTabKeys} sx={{display: "flex", gap: "5px", flexWrap:'wrap', position: "relative"}}>{slidingIndicator}{tabs}</Box>
					</VBox>
					{decoration}
				</HBox>
				{spaceBottom && <Spacer size={`${spaceBottom}px`} />}

				{current}
			</>
		);
	}

	return (
		<>
			<Box
				ref={navRef}
				role="tablist"
				onKeyDown={handleTabKeys}
				aria-label={title || "Sections"}
				sx={{
					position: "relative", display: "flex", alignItems: "center",
					background: background ? background : "transparent",
					gap: variant === "modern" ? "7px" : variant === "strong" ? "0px" : !rounds ? "0px" : "10px",
					marginLeft: rounds ? (spaceLeft ? `${spaceLeft}px` : "0px") : undefined,
					...(isMobile ? { overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" } : {}),
					...sx,
				}}>
				{spaceLeft && !rounds && <Spacer size={`${spaceLeft}px`} />}

				{slidingIndicator}{tabs}

				{!rounds && variant !== "modern" && (
					<Box
						sx={{
						borderBottom: variant === "strong" ? "3px solid rgba(0,0,0,.1)" : "2px solid rgba(0,0,0,.1)",
							flexGrow: "1",
						}}></Box>
				)}
				{decoration}
			</Box>
			{spaceBottom && <Spacer size={`${spaceBottom}px`} />}

			{current}
		</>
	);
};

export const TabItem: React.FC<ITabItemProps> = ({ children }) => {
	return <>{children}</>;
};
